import { get } from 'svelte/store';
import { ApiTypes } from '../../enums/api.enum';
import { WidgetType } from '../../enums/widget-type.enum';
import { WindowsCustomEventTypes } from '../../enums/windows-custom-event.enum';
import {
  addBusyApi,
  removeApiError,
  removeBusyApi,
} from '../../helpers/handle-api';
import type { IAnswer } from '../../interfaces/answer.interface';
import type { IRequestSearch } from '../../interfaces/request-search-question.interface';
import type { IStreamQuestion } from '../../interfaces/stream-question.interface';
import { ConfigStore } from '../../store/config';
import {
  state_answerRating,
  state_busyApi,
  state_conversationResults,
  state_scrollingBehavior,
  state_searchResult,
  state_searchString,
  state_questionImageSearch,
  state_searchSuggestion,
  state_selectedMode,
  state_showSearch,
  state_updatedAttributes,
} from '../../store/state';
import { defaultLogger as logger } from '../../telemetry/logger';
import { postQuestion } from '../api/post-question';
import { postQuestionStream } from '../api/post-question-stream';
import { AlternativeHelper } from './alternative.handler';
import { QuestionRelatedSuggestionsHandler } from './question-related-suggestions.handler';
import { ConversationHistoryHelper } from '../../helpers/conversation-history-helper';
import { CitationHelper } from '../../helpers/citation-helper';
import type { IToolResponse } from '../../interfaces/tool-response.interface';
import type { IToolCall } from '../../interfaces/tool-call.interface';
import { MarkupHelper } from '../../helpers/markup-helper';
import { ThreadHandler } from './thread.handler';
import {
  conversationConnection,
  type ConversationHttpError,
} from '../api/conversation-connection';
import type { IConversationEvent } from '../../interfaces/conversation-event.interface';
import type { IOAuthAuthorizationRequest } from '../../interfaces/oauth-authorization.interface';
import { ChatKitWidgetHelper } from '../../helpers/chatkit-widget-helper';
import { ChatKitWidgetCache } from '../../helpers/chatkit-widget-cache';
import { pendingWidgetRecovery } from '../../helpers/pending-widget-recovery';
import type { IChatKitWidgetInstance } from '../../interfaces/chatkit-widget.interface';

export class AnswerHelper {
  private questionRelatedSuggestionsHandler: QuestionRelatedSuggestionsHandler;
  private alternativeHelper: AlternativeHelper;

  private md = new MarkupHelper();

  private abortController: AbortController;
  private activeSseReader?: ReadableStreamDefaultReader;
  private lastStreamRenderTime: number | null = null;

  // this function is called at the end of a stream, possibly to continue the stream
  // - required by LLM function call system
  private endOfStreamAction?: () => void | Promise<void>;

  // recursion counter to prevent infinite loops
  // - required by LLM function call system
  private recursionCounter: number;
  private persistentTurns = new Map<string, IAnswer>();
  private persistentRequestContext = new Map<
    string,
    { attributes: any; flags: string[] }
  >();
  private pendingQuestionFiles?: { id: string; previewUrl: string }[];
  private activeClientMessageId?: string;
  private activeThreadId?: string;
  private cancelledClientMessageIds = new Set<string>();
  private cancelledRequestIds = new Set<string>();
  private pendingAuthorizations = new Map<
    string,
    {
      toolCalls: IToolCall[];
      questionId: string;
      attributes: any;
      flags: string[];
      transport: 'legacy' | 'persistent';
    }
  >();
  private resumedAuthorizationQuestions = new Set<string>();
  private attemptedWidgetRecoveries = new Set<string>();
  private widgetRecovery?: { clientMessageId: string; done: Promise<void>; finish: () => void };

  constructor() {
    this.questionRelatedSuggestionsHandler =
      new QuestionRelatedSuggestionsHandler();
    this.alternativeHelper = new AlternativeHelper();
    this.abortController = new AbortController();
    this.recursionCounter = 0;
    conversationConnection.subscribe((event) =>
      this.processPersistentEvent(event)
    );
    pendingWidgetRecovery.subscribe((turn) => { void this.recoverPendingWidgets(turn); });

    this.abort();
  }

  public async get(params: IRequestSearch): Promise<void> {
    this.pendingQuestionFiles = get(state_questionImageSearch)
      .filter((attachment) => attachment.status === 'ready' && attachment.id)
      .map((attachment) => ({
        id: attachment.id!,
        previewUrl: attachment.previewUrl,
      }));
    if (params.requestType !== 'greeting') await this.widgetRecovery?.done;
    // inject the attributes of the currently selected mode switch option into
    // every greeting / user input sent to the backend (current mode wins on
    // key conflicts with attributes from widget initialisation). Resolve the
    // default here as well so the initial greeting is covered even if it fires
    // before the mode switch component has mounted.
    const config = get(ConfigStore);
    const options = config.modeSwitchOptions ?? [];
    const selectedMode =
      get(state_selectedMode) ??
      (options.length
        ? (options.find((o) => o.label === config.modeSwitchDefault) ??
          options[0])
        : null);
    // every attribute key that any mode can contribute, so a previously
    // selected mode's keys can be stripped when switching to a mode that
    // doesn't define them (avoids stale, disjoint mode attributes leaking)
    const modeAttributeKeys = new Set(
      options.flatMap((o) => Object.keys(o.attributes ?? {}))
    );
    if (selectedMode?.attributes) {
      params.attributes = {
        ...(params.attributes ?? {}),
        ...selectedMode.attributes,
      };
    }

    if (
      [
        WidgetType.chat,
        WidgetType.cb,
        WidgetType.cip,
        WidgetType.ce,
        WidgetType.cmt,
        WidgetType.ctt,
      ].includes(get(ConfigStore).widgetType)
    ) {
      if (
        (get(ConfigStore).widgetType === WidgetType.cb ||
          get(ConfigStore).widgetType === WidgetType.cmt ||
          get(ConfigStore).widgetType === WidgetType.ctt) &&
        !get(state_showSearch) &&
        !get(ConfigStore).greetingOnToggle
      )
        return;

      if (params.requestType === 'greeting') {
        state_updatedAttributes.set(params.attributes);
        if (!ConversationHistoryHelper.isEmpty()) {
          if (ConversationHistoryHelper.getLastTurn().is_greeting) {
            //console.log("Regenerate greeting");
            // clear history & regenerate greeting
            ConversationHistoryHelper.clear();
          } else {
            //console.log("Greeting request when history already populated");
            return;
          }
        }
      } else if (get(state_updatedAttributes) != null) {
        //console.log("Attributes stored from previous request");
        // merge the stored attributes into params.attributes, but drop any
        // mode-switch keys first so only the currently selected mode's
        // attributes (already applied to params.attributes above) are sent
        const storedAttributes = { ...get(state_updatedAttributes) };
        for (const key of modeAttributeKeys) delete storedAttributes[key];
        params.attributes = {
          ...storedAttributes,
          ...params.attributes,
        };
        //state_updatedAttributes.set(null);
        //console.dir(params.attributes);
      }

      if (params.requestType !== 'greeting') {
        const previous = ConversationHistoryHelper.getUnanswered();
        if (previous.respondedToId) {
          await this.handleGetAnswerOnPersistentChat(
            params,
            ConversationHistoryHelper.getThreadId() ?? previous.respondedToId
          );
          return;
        }
        await this.handleBootstrapChat(params);
        return;
      }

      await this.handleGetAnswerOnChatStream(params);
    } else {
      await this.handleGetAnswerOnQnA(params);
    }
  }

  // hand the snapshot to the turn being created, once
  private consumeQuestionFiles(): { id: string; previewUrl: string }[] {
    const files = this.pendingQuestionFiles ?? [];
    this.pendingQuestionFiles = undefined;
    return files;
  }

  private async handleBootstrapChat(params: IRequestSearch): Promise<void> {
    if (!this.validateParams(params, 'handleBootstrapChat')) return;

    this.clearQuestionErrors();
    const cancellation = this.cancelInFlightQuestion();
    const reqObj: IRequestSearch = { ...params };
    const clientMessageId = conversationConnection.createClientMessageId();
    const turn = ConversationHistoryHelper.addQuestion(
      params.query ?? '',
      false,
      false,
      this.consumeQuestionFiles()
    );
    turn.client_message_id = clientMessageId;
    this.activeClientMessageId = clientMessageId;
    state_searchString.set('');
    state_searchSuggestion.set([]);
    this.questionRelatedSuggestionsHandler.clear();
    addBusyApi(ApiTypes.postQuestion);

    try {
      await cancellation;
      const response = await conversationConnection.bootstrap(
        reqObj,
        clientMessageId,
        this.abortController.signal
      );
      if (this.isStaleClientMessage(clientMessageId)) return;
      let data: IAnswer = await response.json();
      data.client_message_id = clientMessageId;
      turn.question_id = data.question_id;
      turn.thread_id = data.thread_id;

      if (data.tool_calls && data.tool_calls.length > 0) {
        this.renderWidgetToolCalls(turn, data.tool_calls);
        const toolResult = await this.executeTools(
          data.tool_calls,
          data.question_id,
          false,
          params.attributes,
          params.flags ?? []
        );
        if (toolResult) {
          data.answer = toolResult.answer;
          data.question_id = toolResult.questionId;
        }
      }

      CitationHelper.addReferences(data);
      data.answer = this.md.render(
        data.answer,
        get(ConfigStore).whitelistedDomains
      );
      Object.assign(turn, data);
      ConversationHistoryHelper.updateLastTurn(turn);
      this.clearQuestionErrors();
      if (data.thread_id) {
        try {
          await conversationConnection.ensureConnected(data.thread_id);
        } catch (connectionError) {
          logger.warn(
            'Conversation created but persistent event connection is unavailable',
            connectionError
          );
        }
      }
    } catch (error) {
      if (this.shouldFallbackPersistent(error)) {
        logger.warn(
          'Persistent bootstrap unavailable; falling back to /1/question',
          error
        );
        const response = await postQuestion(
          reqObj,
          this.abortController.signal
        );
        if (this.isStaleClientMessage(clientMessageId)) return;
        if (response?.ok) {
          const data: IAnswer = await response.json();
          CitationHelper.addReferences(data);
          data.answer = this.md.render(
            data.answer,
            get(ConfigStore).whitelistedDomains
          );
          Object.assign(turn, data);
          ConversationHistoryHelper.updateLastTurn(turn);
          this.clearQuestionErrors();
        }
      } else if (error instanceof Error) {
        if (this.isStaleClientMessage(clientMessageId)) return;
        ConversationHistoryHelper.hideLastTurnOnError(error);
      }
    } finally {
      this.persistentRequestContext.delete(clientMessageId);
      if (!this.isStaleClientMessage(clientMessageId)) {
        if (this.activeClientMessageId === clientMessageId) {
          this.activeClientMessageId = undefined;
        }
        removeBusyApi(ApiTypes.postQuestion);
      }
    }
  }

  private async handleGetAnswerOnPersistentChat(
    params: IRequestSearch,
    threadId: string
  ): Promise<void> {
    if (!this.validateParams(params, 'handleGetAnswerOnPersistentChat')) return;

    this.clearQuestionErrors();
    const cancellation = this.cancelInFlightQuestion();
    this.recursionCounter = 0;
    const reqObj: IRequestSearch = { ...params };
    const lastQuestion = ConversationHistoryHelper.getUnanswered();
    if (lastQuestion.respondedToId) {
      reqObj.response_to_question_id = lastQuestion.respondedToId;
      if (lastQuestion.questionsSoFar == 1 && reqObj.requestType === 'turnX') {
        reqObj.requestType = 'turn1';
      }
    }
    if (
      lastQuestion.unrespondedCompilation &&
      lastQuestion.unrespondedCompilation != params.query
    ) {
      reqObj.query = lastQuestion.unrespondedCompilation + '\n' + params.query;
      if (reqObj.query.length > get(ConfigStore).searchStringMaxLength) {
        reqObj.query = reqObj.query.substring(
          0,
          get(ConfigStore).searchStringMaxLength
        );
      }
    }

    const clientMessageId = conversationConnection.createClientMessageId();
    const turn = ConversationHistoryHelper.addQuestion(
      params.query ?? '',
      false,
      true,
      this.consumeQuestionFiles()
    );
    turn.client_message_id = clientMessageId;
    this.activeClientMessageId = clientMessageId;
    this.activeThreadId = threadId;
    this.persistentRequestContext.set(clientMessageId, {
      attributes: reqObj.attributes,
      flags: reqObj.flags ?? [],
    });
    state_searchString.set('');
    state_searchSuggestion.set([]);
    state_scrollingBehavior.set(!get(state_scrollingBehavior));
    this.questionRelatedSuggestionsHandler.clear();
    addBusyApi(ApiTypes.streamPOSTQuestion);

    const wasConnected = conversationConnection.isConnected(threadId);
    let persistentUnavailable = false;
    try {
      await cancellation;
      await conversationConnection.ensureConnected(threadId);
      if (this.isStaleClientMessage(clientMessageId)) return;
      if (
        !wasConnected &&
        ConversationHistoryHelper.getThreadId() === threadId
      ) {
        await new ThreadHandler().get(true);
      }
    } catch (error) {
      if (this.isStaleClientMessage(clientMessageId)) return;
      if (!this.shouldFallbackPersistent(error)) {
        removeBusyApi(ApiTypes.streamPOSTQuestion);
        this.persistentRequestContext.delete(clientMessageId);
        throw error;
      }
      persistentUnavailable = true;
    }

    if (persistentUnavailable) {
      removeBusyApi(ApiTypes.streamPOSTQuestion);
      this.persistentRequestContext.delete(clientMessageId);
      try {
        await this.processQuestionStream(reqObj, turn);
      } catch (error) {
        if (this.isStaleClientMessage(clientMessageId)) return;
        if (error instanceof Error) {
          ConversationHistoryHelper.hideLastTurnOnError(error);
        }
      }
      return;
    }

    try {
      const accepted = await this.sendPersistentQuestion(
        threadId,
        reqObj,
        clientMessageId,
        this.abortController.signal
      );
      if (this.isStaleClientMessage(clientMessageId)) {
        if (accepted.request_id) {
          this.cancelledRequestIds.add(accepted.request_id);
          await this.cancelPersistentQuestion(threadId, {
            requestId: accepted.request_id,
            clientMessageId,
          });
        }
        return;
      }
      turn.request_id = accepted.request_id;
      this.persistentTurns.set(accepted.request_id, turn);
    } catch (error) {
      if (
        this.isStaleClientMessage(clientMessageId) ||
        (error as any)?.name === 'AbortError'
      ) {
        return;
      }
      removeBusyApi(ApiTypes.streamPOSTQuestion);
      this.persistentRequestContext.delete(clientMessageId);
      if (this.shouldFallbackPersistent(error)) {
        logger.warn(
          'Persistent conversation unavailable; falling back to request SSE',
          error
        );
        await this.processQuestionStream(reqObj, turn);
      } else if (error instanceof Error) {
        this.markTurnSubmissionError(turn, error);
        if ((error as any).status === 409) {
          await new ThreadHandler().get();
        }
      }
    }
  }

  private shouldFallbackPersistent(error: unknown): boolean {
    const status = (error as any)?.status;
    return (
      (error as any)?.name === 'PersistentUnavailableError' ||
      status === 404 ||
      status === 405
    );
  }

  private async sendPersistentQuestion(
    threadId: string,
    reqObj: IRequestSearch,
    clientMessageId: string,
    signal: AbortSignal
  ): Promise<{ request_id: string; thread_id: string }> {
    for (let attempt = 0; attempt < 3; attempt += 1) {
      try {
        return await conversationConnection.sendQuestion(
          threadId,
          reqObj,
          clientMessageId,
          signal
        );
      } catch (error) {
        if ((error as any)?.status !== 409 || attempt === 2) {
          throw error;
        }
        const latestQuestionId = (error as ConversationHttpError)
          ?.latestQuestionId;
        if (
          latestQuestionId &&
          latestQuestionId !== reqObj.response_to_question_id
        ) {
          logger.info(
            'Retrying persistent conversation with latest question id',
            {
              previous_response_to_question_id: reqObj.response_to_question_id,
              latest_question_id: latestQuestionId,
            }
          );
          reqObj.response_to_question_id = latestQuestionId;
          continue;
        }
        await new Promise((resolve) => setTimeout(resolve, 400));
      }
    }
    throw new Error('Unable to submit persistent conversation question');
  }

  private markTurnSubmissionError(turn: IAnswer, error: Error): void {
    turn.is_streaming = false;
    turn.is_streamError = this.persistentErrorMessage(error);
    ConversationHistoryHelper.updateTurn(turn);
  }

  private persistentErrorMessage(error: Error): string {
    const httpError = error as ConversationHttpError;
    return (
      httpError.serverMessage ??
      httpError.body?.message ??
      error.message ??
      'Conversation request failed'
    );
  }

  private async processPersistentEvent(
    event: IConversationEvent
  ): Promise<void> {
    if (event.type === 'ready') {
      if (event.reconnected) {
        await new ThreadHandler().get(true);
        if (!get(state_conversationResults).some((turn) => turn.is_streaming)) {
          this.persistentTurns.clear();
          this.persistentRequestContext.clear();
          removeBusyApi(ApiTypes.streamPOSTQuestion);
        }
      }
      const pending = await conversationConnection.getPendingAuthorizations(
        event.thread_id
      );
      for (const authorization of pending) {
        await this.processPersistentEvent({
          type: 'authorization.required',
          event_id: `pending-${authorization.transaction_id}`,
          thread_id: event.thread_id,
          source: 'server',
          timestamp: new Date().toISOString(),
          payload: authorization,
        });
      }
      return;
    }

    if (this.isCancelledPersistentEvent(event)) {
      this.forgetPersistentEvent(event);
      return;
    }

    if (event.type.startsWith('authorization.')) {
      const authorization = event.payload as IOAuthAuthorizationRequest;
      let turns = get(state_conversationResults);
      let turn =
        turns.find(
          (candidate) =>
            candidate.question_id === authorization.response_to_question_id
        ) ??
        turns.find((candidate) =>
          candidate.authorization_requests?.some(
            (request) => request.transaction_id === authorization.transaction_id
          )
        );
      if (!turn && authorization.response_to_question_id) {
        await new ThreadHandler().get(true);
        turns = get(state_conversationResults);
        turn =
          turns.find(
            (candidate) =>
              candidate.question_id === authorization.response_to_question_id
          ) ??
          turns.find((candidate) =>
            candidate.authorization_requests?.some(
              (request) =>
                request.transaction_id === authorization.transaction_id
            )
          );
      }
      if (!turn) {
        logger.warn(
          'OAuth authorization event did not match a conversation turn',
          {
            event_type: event.type,
            transaction_id: authorization.transaction_id,
            response_to_question_id: authorization.response_to_question_id,
            thread_id: event.thread_id,
          }
        );
        return;
      }

      const existing = turn.authorization_requests?.find(
        (request) => request.transaction_id === authorization.transaction_id
      );
      if (existing) {
        Object.assign(existing, authorization);
      } else {
        turn.authorization_requests = [
          ...(turn.authorization_requests ?? []),
          authorization,
        ];
      }
      turn.authorization_requests?.forEach((request) => {
        if (request.transaction_id === authorization.transaction_id) {
          request.status =
            event.type === 'authorization.required'
              ? 'pending'
              : (event.type.substring(
                  'authorization.'.length
                ) as IOAuthAuthorizationRequest['status']);
        }
      });
      state_conversationResults.set([...turns]);

      const ownsTurn =
        !event.owner_client_id ||
        event.owner_client_id === conversationConnection.getClientId();
      logger.info('OAuth authorization event received', {
        event_type: event.type,
        transaction_id: authorization.transaction_id,
        response_to_question_id: authorization.response_to_question_id,
        owner_client_id: event.owner_client_id,
        client_id: conversationConnection.getClientId(),
        owns_turn: ownsTurn,
      });
      if (event.type === 'authorization.succeeded' && ownsTurn) {
        await this.resumeAuthorizedTools(authorization, turn);
      } else if (
        (event.type === 'authorization.cancelled' ||
          event.type === 'authorization.failed') &&
        ownsTurn
      ) {
        await this.resumeDeclinedAuthorization(authorization, turn);
      } else if (!ownsTurn) {
        logger.info('OAuth authorization event belongs to another client', {
          event_type: event.type,
          transaction_id: authorization.transaction_id,
          owner_client_id: event.owner_client_id,
          client_id: conversationConnection.getClientId(),
        });
      }
      return;
    }

    if (event.type === 'turn.started') {
      const payload = event.payload as
        | {
            question?: string;
          }
        | undefined;
      let turn = get(state_conversationResults).find(
        (candidate) =>
          !!event.client_message_id &&
          candidate.client_message_id === event.client_message_id
      );
      if (!turn) {
        const question = payload?.question?.trim() ? payload.question : '';
        turn = ConversationHistoryHelper.addQuestion(question, false, true);
        turn.client_message_id = event.client_message_id;
      }
      turn.request_id = event.request_id;
      if (event.request_id) this.persistentTurns.set(event.request_id, turn);
      addBusyApi(ApiTypes.streamPOSTQuestion);
      return;
    }

    if (event.type === 'turn.completed' || event.type === 'turn.error') {
      if (this.widgetRecovery && event.client_message_id === this.widgetRecovery.clientMessageId) this.widgetRecovery.finish();
      const turn = event.request_id
        ? this.persistentTurns.get(event.request_id)
        : undefined;
      if (turn && event.type === 'turn.error') {
        const payload = event.payload as
          | {
              message?: string;
              user_message?: string;
              error_code?: string;
              notice_code?: string;
              notice_reason?: string;
            }
          | undefined;
        turn.is_streaming = false;
        // `message` is diagnostic server detail and may contain implementation
        // information. Only explicitly user-safe text should reach the chat UI.
        turn.is_streamError =
          payload?.user_message ?? payload?.error_code ?? 'general';
        if (payload?.notice_code === 'history_reset') {
          turn.notices = [{
            code: 'history_reset',
            reason: payload.notice_reason ?? 'contract_changed',
          }];
        }
        ConversationHistoryHelper.updateTurn(turn);
      } else if (turn && event.type === 'turn.completed') {
        this.clearQuestionErrors();
        ConversationHistoryHelper.stopStreaming(turn.position_in_thread, true);
      }
      this.finishPersistentTurn(event);
      return;
    }

    if (event.type !== 'answer.chunk') return;

    let turn = event.request_id
      ? this.persistentTurns.get(event.request_id)
      : undefined;
    if (!turn) {
      turn = get(state_conversationResults).find(
        (candidate) =>
          !!event.client_message_id &&
          candidate.client_message_id === event.client_message_id
      );
    }
    if (!turn) {
      turn = ConversationHistoryHelper.addQuestion('', false, true);
      turn.client_message_id = event.client_message_id;
      turn.request_id = event.request_id;
      if (event.request_id) this.persistentTurns.set(event.request_id, turn);
    }

    const data = {
      ...(event.payload as IStreamQuestion),
      delta: {
        ...(event.payload as IStreamQuestion)?.delta,
      },
    };
    const ownsTurn =
      !event.owner_client_id ||
      event.owner_client_id === conversationConnection.getClientId();
    if (!ownsTurn) {
      delete data.tool_calls;
      if (data.delta) delete data.delta.tool_calls;
    }

    const requestContext = event.client_message_id
      ? this.persistentRequestContext.get(event.client_message_id)
      : undefined;
    const isFinished = data.delta?.is_finished === true;
    try {
      await this.processStreamMessage(
        data,
        turn,
        requestContext?.attributes,
        requestContext?.flags ?? [],
        'persistent'
      );
    } finally {
      if (isFinished) {
        this.clearQuestionErrors();
        ConversationHistoryHelper.stopStreaming(turn.position_in_thread, true);
        if (ownsTurn && this.endOfStreamAction) {
          const action = this.endOfStreamAction;
          this.endOfStreamAction = undefined;
          try {
            await action();
          } catch (error) {
            logger.error('Persistent end-of-stream action failed', error);
          }
        }
        this.finishPersistentTurn(event);
      }
    }
  }

  private finishPersistentTurn(event: IConversationEvent): void {
    removeBusyApi(ApiTypes.streamPOSTQuestion);
    if (event.request_id) {
      this.persistentTurns.delete(event.request_id);
      this.cancelledRequestIds.delete(event.request_id);
    }
    if (event.client_message_id) {
      this.persistentRequestContext.delete(event.client_message_id);
      this.cancelledClientMessageIds.delete(event.client_message_id);
      if (this.activeClientMessageId === event.client_message_id) {
        this.activeClientMessageId = undefined;
        this.activeThreadId = undefined;
      }
    }
  }

  private async cancelInFlightQuestion(): Promise<void> {
    const cancellations: Promise<void>[] = [];
    const activeThreadId =
      this.activeThreadId ?? ConversationHistoryHelper.getThreadId();
    const activeClientMessageId = this.activeClientMessageId;

    if (activeThreadId && activeClientMessageId) {
      cancellations.push(
        this.cancelPersistentQuestion(activeThreadId, {
          clientMessageId: activeClientMessageId,
        })
      );
    }

    this.activeSseReader?.cancel().catch((error) => {
      logger.debug('Unable to cancel existing stream reader', error);
    });
    this.abortController?.abort();
    this.abortController = new AbortController();
    this.endOfStreamAction = undefined;

    ConversationHistoryHelper.cancelUnansweredTurns().forEach(
      (clientMessageId) => {
        this.cancelledClientMessageIds.add(clientMessageId);
        this.persistentRequestContext.delete(clientMessageId);
      }
    );

    this.persistentTurns.forEach((turn, requestId) => {
      if (!turn.question_id || turn.is_streaming) {
        if (turn.client_message_id) {
          this.cancelledClientMessageIds.add(turn.client_message_id);
          this.persistentRequestContext.delete(turn.client_message_id);
        }
        this.cancelledRequestIds.add(requestId);
        if (activeThreadId) {
          cancellations.push(
            this.cancelPersistentQuestion(activeThreadId, {
              requestId,
              clientMessageId: turn.client_message_id,
            })
          );
        }
        this.persistentTurns.delete(requestId);
      }
    });

    removeBusyApi(ApiTypes.postQuestion);
    removeBusyApi(ApiTypes.streamPOSTQuestion);
    this.activeClientMessageId = undefined;
    this.activeThreadId = undefined;

    await this.waitForCancellations(cancellations);
  }

  private clearQuestionErrors(): void {
    removeApiError(ApiTypes.postQuestion);
    removeApiError(ApiTypes.streamPOSTQuestion);
  }

  private async cancelPersistentQuestion(
    threadId: string,
    cancellation: { requestId?: string; clientMessageId?: string }
  ): Promise<void> {
    try {
      await conversationConnection.cancelQuestion(threadId, cancellation);
    } catch (error) {
      logger.warn('Unable to cancel persistent conversation turn', error);
    }
  }

  private async waitForCancellations(
    cancellations: Promise<void>[]
  ): Promise<void> {
    if (cancellations.length === 0) return;
    await Promise.race([
      Promise.allSettled(cancellations),
      new Promise<void>((resolve) => setTimeout(resolve, 1500)),
    ]);
  }

  private canMutateTurn(turn: IAnswer): boolean {
    return (
      !turn.client_message_id ||
      !this.isStaleClientMessage(turn.client_message_id)
    );
  }

  private isStaleClientMessage(clientMessageId: string): boolean {
    return (
      this.cancelledClientMessageIds.has(clientMessageId) ||
      (!!this.activeClientMessageId &&
        this.activeClientMessageId !== clientMessageId)
    );
  }

  private isCancelledPersistentEvent(event: IConversationEvent): boolean {
    return (
      (!!event.client_message_id &&
        this.cancelledClientMessageIds.has(event.client_message_id)) ||
      (!!event.request_id && this.cancelledRequestIds.has(event.request_id))
    );
  }

  private forgetPersistentEvent(event: IConversationEvent): void {
    if (event.request_id) {
      this.persistentTurns.delete(event.request_id);
    }
    if (event.client_message_id) {
      this.persistentRequestContext.delete(event.client_message_id);
    }
  }

  /** get answer when the view isn't not chat */
  private async handleGetAnswerOnQnA(params: IRequestSearch): Promise<void> {
    if (!this.validateParams(params, 'getAnswerOnQnA')) return;

    this.clearQuestionErrors();
    let reqObj: IRequestSearch = { ...params };

    try {
      state_searchString.set(params.query ?? '');
      state_scrollingBehavior.set(!get(state_scrollingBehavior));
      state_searchSuggestion.set([]);
      this.questionRelatedSuggestionsHandler.clear();
      // this.store.set({ ...get(Store), searchSuggestion: [] });

      this.abortController?.abort();
      this.abortController = new AbortController();
      const res = await postQuestion(reqObj, this.abortController.signal);

      if (res?.ok) {
        const data: IAnswer = await res.json();
        this.clearQuestionErrors();

        state_searchResult.set(
          params?.requestType === 'greeting'
            ? {
                ...data,
                is_greeting: params?.requestType === 'greeting',
              }
            : data
        );

        // if (!params?.skipAlternative)
        //     this.alternativeHelper.get(params)

        if (
          get(ConfigStore).suggestQuestions === 'always' ||
          (get(ConfigStore).suggestQuestions === 'greeting' &&
            params?.requestType === 'greeting') ||
          (get(ConfigStore).suggestQuestions === 'interaction' &&
            params?.requestType !== 'greeting')
        ) {
          this.questionRelatedSuggestionsHandler.get(
            data,
            params?.requestType === 'greeting'
          );
        }
      }
    } catch (e) {
      logger.error('AnswerHelper/getAnswerOnQnA', e);
    }
  }

  /** get answer when the view is chat */
  private async handleGetAnswerOnChatStream(
    params: IRequestSearch
  ): Promise<void> {
    if (!this.validateParams(params, 'getAnswerOnChatStream')) return;

    this.clearQuestionErrors();
    this.recursionCounter = 0;
    let reqObj: IRequestSearch = { ...params };

    if (!!get(ConfigStore)?.serverConfig?.id)
      reqObj.config = get(ConfigStore)?.serverConfig?.id;

    try {
      await this.activeSseReader?.cancel();
      this.abortController?.abort();
      this.abortController = new AbortController();

      const lastQuestion = ConversationHistoryHelper.getUnanswered();
      if (lastQuestion.respondedToId) {
        reqObj.response_to_question_id = lastQuestion.respondedToId;
        logger.debug('Questions so far: ' + lastQuestion.questionsSoFar);
        if (
          lastQuestion.questionsSoFar == 1 &&
          reqObj.requestType === 'turnX'
        ) {
          reqObj.requestType = 'turn1';
        }
      }
      if (lastQuestion.unrespondedCompilation) {
        reqObj.query =
          lastQuestion.unrespondedCompilation + '\n' + params.query;
        if (reqObj.query.length > get(ConfigStore).searchStringMaxLength) {
          logger.warn(
            'Query length ' +
              reqObj.query.length +
              ' exceeds maximum allowed length, truncating query'
          );
          reqObj.query = reqObj.query.substring(
            0,
            get(ConfigStore).searchStringMaxLength
          );
        }
      }
      //console.log("STREAMING REQUEST - LAST CONV: " + JSON.stringify(lastQuestion));

      const turn: IAnswer = ConversationHistoryHelper.addQuestion(
        params.query ?? '',
        params?.requestType === 'greeting',
        true,
        this.consumeQuestionFiles()
      );
      state_searchString.set('');
      state_searchSuggestion.set([]);
      state_scrollingBehavior.set(!get(state_scrollingBehavior));

      this.questionRelatedSuggestionsHandler.clear();

      await this.processQuestionStream(reqObj, turn);

      if (params?.requestType === 'greeting') {
        window.dispatchEvent(
          new CustomEvent(WindowsCustomEventTypes.greetingInitCompleted)
        );
      }
    } catch (e) {
      if (e instanceof Error) {
        ConversationHistoryHelper.hideLastTurnOnError(e);
      } else {
        logger.error('Unexpected error type:', e);
      }
      //ConversationHistoryHelper.stopStreaming();
      logger.error('AnswerHelper/getAnswerOnChatStream', e);
    }
  }

  private async processQuestionStream(
    reqObj: IRequestSearch,
    turn: IAnswer
  ): Promise<void> {
    let res;

    addBusyApi(ApiTypes.streamPOSTQuestion);

    try {
      res = await postQuestionStream(reqObj, this.abortController.signal);
    } catch (e: any) {
      if (this.canMutateTurn(turn)) {
        ConversationHistoryHelper.stopStreaming(turn.position_in_thread, false);
        if (e.name != 'AbortError') removeBusyApi(ApiTypes.streamPOSTQuestion);
      }
      logger.debug(
        'Stopping stream for thread item #' +
          turn.position_in_thread +
          ' due to error: ',
        e.message
      );
      throw e;
    }

    const reader = <ReadableStreamDefaultReader>res?.body.getReader();
    this.activeSseReader = reader;

    let buffer = '';
    let eventBuffer = '';

    try {
      while (true) {
        if (!this.canMutateTurn(turn)) {
          await reader.cancel();
          break;
        }

        const { value, done } = await reader.read();

        if (done) {
          //logger.debug("SSE - End of stream");
          ConversationHistoryHelper.stopStreaming(
            turn.position_in_thread,
            true
          );
          logger.debug(
            'Stopping stream for thread item #' +
              turn.position_in_thread +
              ' due to end of stream'
          );
          if (this.endOfStreamAction) {
            const action = this.endOfStreamAction;
            this.endOfStreamAction = undefined;
            await action();
          }
          removeBusyApi(ApiTypes.streamPOSTQuestion);
          this.clearQuestionErrors();
          if (this.activeSseReader === reader) {
            this.activeSseReader = undefined;
          }
          break;
        }

        buffer += new TextDecoder().decode(value);

        let lines = buffer.split('\n');
        buffer = lines.pop() || ''; // Keep the last partial line in the buffer

        for (let line of lines) {
          if (line.trim() === '') {
            // End of an event
            const eventData = eventBuffer.trim();
            if (eventData) {
              logger.debug('SSE: ' + eventData);
              try {
                const data: IStreamQuestion = JSON.parse(eventData);
                await this.processStreamMessage(
                  data,
                  turn,
                  reqObj.attributes,
                  reqObj.flags ?? []
                );

                if (data?.delta?.is_finished) {
                  //logger.debug("SSE - End of delta");
                  //removeBusyApi(ApiTypes.streamPOSTQuestion);
                  await reader.cancel();
                }
              } catch (e) {
                logger.error('SSE: Error parsing JSON', e);
                logger.error('SSE: Erroneous JSON: ' + eventData);
              }
            }
            eventBuffer = '';
          } else if (line.startsWith('data:')) {
            eventBuffer += line.slice(5).trim() + '\n';
          }
        }
      }
    } catch (e) {
      logger.error('SSE: Error processing stream', e);
      if (this.canMutateTurn(turn)) {
        removeBusyApi(ApiTypes.streamPOSTQuestion);
      }
      throw e;
      //removeBusyApi(ApiTypes.streamPOSTQuestion);
    } finally {
      if (this.activeSseReader === reader) {
        this.activeSseReader = undefined;
      }
    }

    /*

        this.sseReader = <ReadableStreamDefaultReader>res?.body.getReader();

        try {
            while (true) {
                const { value, done } = await this.sseReader?.read();

                if (done) {
                    //logger.debug("SSE: DONE");
                    removeBusyApi(ApiTypes.streamPOSTQuestion);
                    ConversationHistoryHelper.stopStreaming();
                    if (this.endOfStreamAction) {
                        const action = this.endOfStreamAction;
                        this.endOfStreamAction = null;
                        await action();
                    }
                    break;
                }

                let jsonString = new TextDecoder().decode(value);
                const dataItems = jsonString.split("data:");

                dataItems.forEach(async el => {

                    if (el.trim()) {

                        logger.debug("SSE: " + el);
                        try {
                            const data: IStreamQuestion = JSON.parse(el);
                            await this.processStreamMessage(data, turn);

                            if (data?.delta?.is_finished) {
                                //logger.debug("SSE delta finished");
                                removeBusyApi(ApiTypes.streamPOSTQuestion);
                                await this.sseReader.cancel();
                                return;
                            }
                        } catch (e) {
                            logger.error('SSE: Error parsing JSON', e)
                            logger.error("SSE: Erroneous JSON: " + el);
                        }
                    }

                });
            }
        } catch {
            removeBusyApi(ApiTypes.streamPOSTQuestion);
        }*/
  }

  /** method to abort pending operation */
  private abort(): void {
    /** check for type of action */
    const abortNow = (e: any): void => {
      if (get(state_busyApi).includes(ApiTypes.postQuestion)) {
        this.abortController?.abort();
      }
    };

    [WindowsCustomEventTypes.requestPostOption].forEach((evt) => {
      window.addEventListener(evt, abortNow);
    });
  }

  private validateParams(params: IRequestSearch, type: string): boolean {
    try {
      /** query cannot be empty */
      if (
        typeof params !== 'object' ||
        (!params.hasOwnProperty('query') && params?.requestType !== 'greeting')
      ) {
        logger.warn(`${type} - incomplete argument`, params);
        return false;
      }

      return true;
    } catch (e) {
      logger.warn(`${type} - invalid argument`, e);
      return false;
    }
  }

  private async processStreamMessage(
    data: IStreamQuestion,
    conversationTurn: IAnswer,
    attributes: any,
    requestFlags: string[],
    transport: 'legacy' | 'persistent' = 'legacy'
  ): Promise<void> {
    //let conversationTurn: IAnswer = ConversationHistoryHelper.getLastTurn();
    //console.log('CT1:');console.log(conversationTurn);
    //console.log('CTD:');console.log(data);

    const allowedLinkDomains = get(ConfigStore).whitelistedDomains;

    const delta = { ...data.delta };
    delete data.delta;
    delete data.question; // otherwise the response payload overwrites the question, which is no good if the question is a function call

    if (delta.tool_calls) {
      data.tool_calls = delta.tool_calls;
    }

    if (data.tool_calls) {
      //console.log("Tool calls: " + JSON.stringify(data.tool_calls));
      if (conversationTurn.tool_calls) {
        //console.log("Existing tool calls: " + JSON.stringify(conversationTurn.tool_calls));
        // merge conversationTurn.tool_calls with data.tool_calls
        data.tool_calls.forEach((tc) => {
          let matchingTC = conversationTurn.tool_calls?.find(
            (t) => t.index == tc.index
          );
          if (matchingTC) {
            if (matchingTC.function == null && tc.function != null) {
              matchingTC.function = tc.function;
            } else if (matchingTC.function != null) {
              matchingTC.function.name =
                matchingTC.function.name != null || tc.function?.name != null
                  ? (matchingTC.function.name || '') + (tc.function?.name || '')
                  : null;
              matchingTC.function.arguments =
                matchingTC.function.arguments != null ||
                tc.function?.arguments != null
                  ? (matchingTC.function.arguments || '') +
                    (tc.function?.arguments || '')
                  : null;
            } else {
              // should not happen
              logger.error('Tool call function is null', tc);
              matchingTC.function = tc.function;
            }
          } else {
            conversationTurn.tool_calls?.push(tc);
          }
        });
      } else {
        conversationTurn.tool_calls = data.tool_calls;
      }
    }

    if (delta.authorization_requests) {
      conversationTurn.authorization_requests = delta.authorization_requests;
    }

    /*if (data.tool_calls && !delta.answer) {
            if (!delta.answer && conversationTurn.answer_chunks.length == 0) {
                const funcr = await this.executeTools(data.tool_calls, data.question_id ? data.question_id : '', get(ConfigStore).sendFunctionResult);
                logger.debug('Returning JavaScript function results', funcr);
                if (funcr != null) {
                    data.answer = funcr.answer;
                    data.question_id = funcr.questionId;
                }
            } else {
                logger.warn("Function call is combined with text response", data.tool_calls);
            }
        }*/

    if (data.stream) {
      //conversationTurn = { ...conversationTurn, is_streaming: true, ...data };
      conversationTurn.is_streaming = true;
    }

    if (data.document_id) {
      conversationTurn.document_data = data.document_data;
      conversationTurn.document_id = data.document_id;
      conversationTurn.document_index = data.document_index;
    }

    if (data.notes) {
      conversationTurn.notes = data.notes;
    }

    if (data.notices) {
      conversationTurn.notices = data.notices;
    }

    if (delta.answer || delta.reasoning) {
      let newChunk = {
        content: delta.answer,
        reasoning: delta.reasoning,
        order: data.order,
      };
      let previousChunk =
        conversationTurn.answer_chunks.length > 0
          ? conversationTurn.answer_chunks[
              conversationTurn.answer_chunks.length - 1
            ]
          : null;

      conversationTurn.answer_chunks.push(newChunk);
      //if (previousChunk && previousChunk.order > newChunk.order) {
      // Sort the chunk array based on the timestamp in ascending order
      conversationTurn.answer_chunks.sort((a, b) => a.order - b.order);

      //console.log("PreviousTS: " + previousChunk?.content + " - " + previousChunk?.order);
      //console.log("newTS: " + newChunk.content + " - " + newChunk.order);

      // Extract content and join them
      CitationHelper.addReferencesAndAnswer(conversationTurn);
      /*} else {
        CitationHelper.addReferencesChunked(conversationTurn, newChunk);
      }*/

      // Extract content and join them
      //const resultString = conversationTurn.answer_chunks.map(chunk => chunk.content).join('');

      // Update the answer
      //conversationTurn.answer = resultString;
    }

    // clear chat bubble if tool calls are present
    // if (!delta.is_finished && delta.tool_results) {
    //   conversationTurn.answer_chunks = [];
    // }

    if (!delta.is_finished && delta.tool_results) {
      conversationTurn.answer_chunks.push({
        content: '\n\n',
        reasoning: '\n\n',
        order:
          conversationTurn.answer_chunks.length > 0
            ? conversationTurn.answer_chunks[
                conversationTurn.answer_chunks.length - 1
              ].order + 0.1
            : -1,
      });
    }

    if (delta.is_finished) {
      // clear input field is not equal to question
      if (get(state_searchString) === data.question) state_searchString.set('');

      state_answerRating.set(null);

      /*switch (get(ConfigStore).filterCitations) {
                case 'no':
                    conversationTurn = { ...conversationTurn, is_streaming: false, ...data, is_streamError: delta?.error };
                    break;
                case 'link':
                    {
                        //console.log ("final run: " + conversationTurn.answer_chunks.map(chunk => chunk.content).join(''));
                        let extracted: { text: string, ids: string[] } = enrichDocumentCitations(conversationTurn.answer_chunks.map(chunk => chunk.content).join(''), conversationTurn.document_id, conversationTurn.document_data);
                        //console.log(extracted);
                        let scanned: { textWithoutCitation: string, result: boolean } = hasUnfinishedDocumentCitations(extracted.text);
                        //console.log(scanned);
                        selectDocumentIdsInAnswer(conversationTurn, extracted.ids); // remove unused documents from answer
                        conversationTurn = { ...conversationTurn, answer: scanned.textWithoutCitation, has_unfinished_citation: scanned.result, is_streaming: false, ...data, is_streamError: delta?.error };
                    }
                    break;
                case 'remove':
                    {
                        let extracted: { text: string, ids: string[] } = extractDocumentCitations(conversationTurn.answer_chunks.map(chunk => chunk.content).join(''), conversationTurn.document_id, conversationTurn.document_data);
                        let scanned: { textWithoutCitation: string, result: boolean } = hasUnfinishedDocumentCitations(extracted.text);
                        selectDocumentIdsInAnswer(conversationTurn, extracted.ids); // remove unused documents from answer
                        conversationTurn = { ...conversationTurn, answer: scanned.textWithoutCitation, has_unfinished_citation: scanned.result, is_streaming: false, ...data, is_streamError: delta?.error };
                    }
                    break;
            }*/

      conversationTurn = {
        ...conversationTurn,
        is_streaming: false,
        ...data,
        is_streamError: delta?.error,
      };

      if (conversationTurn.authorization_requests?.length) {
        ConversationHistoryHelper.updateTurn(conversationTurn);
      }

      //console.log('CTF:');console.dir(conversationTurn);

      if (
        conversationTurn.tool_calls &&
        conversationTurn.tool_calls.length > 0
      ) {
        this.renderWidgetToolCalls(
          conversationTurn,
          conversationTurn.tool_calls
        );
        if (conversationTurn.answer && conversationTurn.answer.length > 0) {
          logger.warn(
            'Function call is combined with text response: ' +
              conversationTurn.answer,
            data.tool_calls
          );
        }
        const funcr = await this.executeTools(
          conversationTurn.tool_calls,
          conversationTurn.question_id,
          true,
          attributes,
          requestFlags,
          transport
        );
        logger.debug('Returning tool call results', funcr);
        /*if (funcr) {
                    conversationTurn.answer = funcr.answer;
                    conversationTurn.question_id = funcr.questionId;
                }*/
      }

      logger.debug('Formatting final answer...');
      conversationTurn.answer =
        data.answer ||
        conversationTurn.answer_chunks.map((chunk) => chunk.content).join(''); // if data.answer exists, this was a cached response and there are not answer_chunks
      conversationTurn.reasoning =
        data.reasoning ||
        conversationTurn.answer_chunks
          .map((chunk) => chunk.reasoning || '')
          .join('');
      CitationHelper.addReferences(conversationTurn);
      conversationTurn.answer = this.md.render(
        conversationTurn.answer,
        allowedLinkDomains
      );
      //if (conversationTurn.reasoning) conversationTurn.reasoning = this.md.render(conversationTurn.reasoning, allowedLinkDomains);

      if (
        get(ConfigStore).suggestQuestions === 'always' ||
        (get(ConfigStore).suggestQuestions === 'greeting' &&
          conversationTurn.is_greeting) ||
        (get(ConfigStore).suggestQuestions === 'interaction' &&
          !conversationTurn.is_greeting)
      ) {
        if (data.question_id)
          this.questionRelatedSuggestionsHandler.get(
            {
              question_id: data.question_id,
            },
            conversationTurn.is_greeting ? conversationTurn.is_greeting : false
          );
      }

      ConversationHistoryHelper.updateTurn(conversationTurn);
    } else {
      if (
        (this.lastStreamRenderTime == null ||
          Date.now() - this.lastStreamRenderTime > 100) &&
        !conversationTurn.has_unfinished_citation
      ) {
        this.lastStreamRenderTime = Date.now();
        conversationTurn.answer = this.md.render(
          conversationTurn.answer,
          allowedLinkDomains
        );
        //if (conversationTurn.reasoning) conversationTurn.reasoning = this.md.render(conversationTurn.reasoning, allowedLinkDomains);
        //console.log("Formatted streamed answer: " + conversationTurn.answer);
        //console.log("Formatted streamed reasoning: " + conversationTurn.reasoning);

        ConversationHistoryHelper.updateTurn(conversationTurn);
      }
    }
  }

  private async executeTools(
    toolCalls: IToolCall[],
    questionId: string,
    isStreaming: boolean,
    attributes: any,
    requestFlags: string[],
    transport: 'legacy' | 'persistent' = 'legacy'
  ): Promise<{
    answer: string;
    questionId: string;
  } | null> {
    logger.debug('Executing JavaScript function(s)', toolCalls);
    const fon: string = get(ConfigStore).functionObjectName;
    const gqa_functions = (window as any)[fon];
    const { widgetToolCalls, normalToolCalls } =
      ChatKitWidgetHelper.splitToolCalls(toolCalls);
    const continuingWidgetToolCalls = widgetToolCalls.filter((toolCall) =>
      ChatKitWidgetHelper.shouldContinueAfterRender(toolCall)
    );
    const requiresContinuation =
      normalToolCalls.length > 0 || continuingWidgetToolCalls.length > 0;
    try {
      // STREAMING / RETURN ====================
      if (isStreaming) {
        if (!requiresContinuation && widgetToolCalls.length) {
          logger.debug('Widget rendered without model continuation', {
            questionId,
            widgets: widgetToolCalls.map((toolCall) => toolCall.function?.name),
          });
          this.endOfStreamAction = undefined;
          return null;
        }

        logger.debug('Add end-of-stream action...', questionId);
        const turn = get(state_conversationResults).find(
          (candidate) => candidate.question_id === questionId
        );
        const pending = turn?.authorization_requests?.filter(
          (request) => !request.status || request.status === 'pending'
        );
        if (pending?.length) {
          this.endOfStreamAction = undefined;
          pending.forEach((request) =>
            this.pendingAuthorizations.set(request.transaction_id, {
              toolCalls,
              questionId,
              attributes,
              flags: requestFlags,
              transport,
            })
          );
          return null;
        }
        this.endOfStreamAction = () =>
          this.continueToolCalls(
            toolCalls,
            questionId,
            attributes,
            requestFlags,
            gqa_functions,
            transport
          );
        return null; // if null, add another turn to the end of the stream
        // NON-STREAMING ====================
      } else {
        if (!requiresContinuation && widgetToolCalls.length) {
          logger.debug('Widget rendered without model continuation', {
            questionId,
            widgets: widgetToolCalls.map((toolCall) => toolCall.function?.name),
          });
          return null;
        }

        let results = await this.buildToolResponses(toolCalls, gqa_functions);
        if (++this.recursionCounter > 3) {
          logger.error(
            'Error executing a JavaScript function',
            'Recursion limit reached'
          );
          return { answer: '', questionId };
        } else if (
          get(ConfigStore).sendFunctionResult ||
          continuingWidgetToolCalls.length > 0
        ) {
          logger.debug(
            'Forwarding JavaScript function results to server (non-streaming)',
            results
          );
          const reqObj: IRequestSearch = {
            query: JSON.stringify(results),
            response_to_question_id: questionId,
            requestType: 'toolcall',
            attributes,
            flags: requestFlags,
            type: 'tool',
          };
          if (get(ConfigStore).functionResultConfig)
            reqObj.config = get(ConfigStore).functionResultConfig;
          const aiResponse = await postQuestion(
            reqObj,
            this.abortController.signal
          );
          if (aiResponse?.ok) {
            let data: IAnswer = await aiResponse.json();
            if (data.tool_calls && !data.answer && data.tool_calls.length > 0) {
              logger.warn(
                'Recursively calling on a JavaScript function',
                data.tool_calls
              );
              const funcr = await this.executeTools(
                data.tool_calls,
                data.question_id,
                false,
                attributes,
                requestFlags
              );
              if (funcr) {
                data.answer = funcr.answer;
                data.question_id = funcr.questionId;
              } else {
                data.answer = '';
                data.question_id = questionId;
              }
            }
            return {
              answer: data.answer,
              questionId: data.question_id,
            };
          } else {
            logger.error(
              'Error sending JavaScript function result back to server'
            );
            return { answer: '', questionId };
          }
        } else {
          if (
            typeof results !== 'undefined' &&
            results !== null &&
            results.length > 0
          ) {
            return {
              answer:
                results.length == 1
                  ? results[0].toString()
                  : results.toString(),
              questionId,
            };
          } else {
            return { answer: '', questionId };
          }
        }
      }
    } catch (e) {
      logger.error('Error executing a JavaScript function', e);
      return { answer: '', questionId };
    }
  }

  private async resumeAuthorizedTools(
    authorization: IOAuthAuthorizationRequest,
    turn: IAnswer
  ): Promise<void> {
    const questionId =
      authorization.response_to_question_id ?? turn.question_id;
    if (!questionId) {
      logger.warn('OAuth authorization resume skipped with no question id', {
        transaction_id: authorization.transaction_id,
        response_to_question_id: authorization.response_to_question_id,
      });
      return;
    }
    if (this.resumedAuthorizationQuestions.has(questionId)) {
      logger.info(
        'OAuth authorization resume skipped because question already resumed',
        {
          transaction_id: authorization.transaction_id,
          question_id: questionId,
        }
      );
      return;
    }
    const incompleteAuthorizations = turn.authorization_requests?.filter(
      (request) => request.status !== 'succeeded'
    );
    if (incompleteAuthorizations?.length) {
      logger.info(
        'OAuth authorization resume waiting for remaining authorizations',
        {
          transaction_id: authorization.transaction_id,
          question_id: questionId,
          statuses: incompleteAuthorizations.map((request) => ({
            transaction_id: request.transaction_id,
            status: request.status,
          })),
        }
      );
      return;
    }
    const pending = this.pendingAuthorizations.get(
      authorization.transaction_id
    );
    const toolCalls = pending?.toolCalls ?? turn.tool_calls ?? [];
    if (!toolCalls.length) {
      logger.warn('OAuth authorization resume skipped with no tool calls', {
        transaction_id: authorization.transaction_id,
        question_id: questionId,
        pending_authorization_known: !!pending,
        turn_has_tool_calls: !!turn.tool_calls?.length,
      });
      return;
    }

    this.resumedAuthorizationQuestions.add(questionId);
    logger.info('OAuth authorization resume posting tool results', {
      transaction_id: authorization.transaction_id,
      question_id: questionId,
      tool_call_count: toolCalls.length,
      pending_authorization_known: !!pending,
    });
    await this.continueToolCalls(
      toolCalls,
      questionId,
      pending?.attributes ?? {},
      pending?.flags ?? [],
      (window as any)[get(ConfigStore).functionObjectName],
      pending?.transport ?? 'persistent'
    );
  }

  private async resumeDeclinedAuthorization(
    authorization: IOAuthAuthorizationRequest,
    turn: IAnswer
  ): Promise<void> {
    const questionId =
      authorization.response_to_question_id ?? turn.question_id;
    const authorizationRequests = turn.authorization_requests ?? [];
    if (
      !questionId ||
      this.resumedAuthorizationQuestions.has(questionId) ||
      authorizationRequests.some(
        (request) => !request.status || request.status === 'pending'
      )
    ) {
      return;
    }

    const pending = this.pendingAuthorizations.get(
      authorization.transaction_id
    );
    const toolCalls = pending?.toolCalls ?? turn.tool_calls ?? [];
    if (!toolCalls.length) return;

    const authorizationResults = new Map<string, unknown>();
    authorizationRequests
      .filter(
        (request) =>
          request.status === 'cancelled' || request.status === 'failed'
      )
      .forEach((request) => {
        authorizationResults.set(request.tool_call_id, {
          error:
            request.status === 'cancelled'
              ? 'oauth_authorization_cancelled'
              : 'oauth_authorization_failed',
          message:
            request.status === 'cancelled'
              ? `The user chose not to authenticate with ${request.display_name}. Acknowledge their choice and do not retry authentication unless they ask.`
              : `Authentication with ${request.display_name} failed. Explain that the requested action could not be completed and offer an alternative if possible.`,
        });
      });
    if (!authorizationResults.size) return;

    this.resumedAuthorizationQuestions.add(questionId);
    await this.continueToolCalls(
      toolCalls,
      questionId,
      pending?.attributes ?? {},
      pending?.flags ?? [],
      (window as any)[get(ConfigStore).functionObjectName],
      pending?.transport ?? 'persistent',
      authorizationResults
    );
  }

  private async continueToolCalls(
    toolCalls: IToolCall[],
    questionId: string,
    attributes: any,
    requestFlags: string[],
    gqa_functions: any,
    transport: 'legacy' | 'persistent',
    suppliedResults: ReadonlyMap<string, unknown> = new Map()
  ): Promise<void> {
    const results = await this.buildToolResponses(
      toolCalls,
      gqa_functions,
      suppliedResults
    );

    if (++this.recursionCounter > 3) {
      logger.error(
        'Error executing a JavaScript function',
        'Recursion limit reached'
      );
      return;
    }

    const reqObj: IRequestSearch = {
      query: JSON.stringify(results),
      response_to_question_id: questionId,
      requestType: 'toolcall',
      attributes,
      flags: requestFlags,
      type: 'tool',
    };

    const nextTurn = ConversationHistoryHelper.addQuestion('', false, true);
    // A legacy parent has no persistent tool reservation. Conversely, never
    // bypass a persistent/OAuth reservation by falling back to the legacy API.
    if (transport === 'legacy') {
      await this.processQuestionStream(reqObj, nextTurn);
      return;
    }
    const threadId =
      ConversationHistoryHelper.getThreadId() ??
      ConversationHistoryHelper.getLastAnsweredId();

    const clientMessageId = conversationConnection.createClientMessageId();
    nextTurn.client_message_id = clientMessageId;
    if (this.widgetRecovery && get(state_conversationResults).some((turn) =>
      turn.client_message_id === this.widgetRecovery?.clientMessageId && turn.question_id === questionId)) {
      this.widgetRecovery.clientMessageId = clientMessageId;
    }
    this.persistentRequestContext.set(clientMessageId, {
      attributes,
      flags: requestFlags,
    });
    try {
      if (!threadId) {
        throw new Error('Unable to continue browser tool calls without a conversation ID');
      }
      const accepted = await conversationConnection.sendQuestion(
        threadId,
        reqObj,
        clientMessageId
      );
      nextTurn.request_id = accepted.request_id;
      this.persistentTurns.set(accepted.request_id, nextTurn);
      addBusyApi(ApiTypes.streamPOSTQuestion);
    } catch (error) {
      if (this.widgetRecovery?.clientMessageId === clientMessageId) this.widgetRecovery.finish();
      this.persistentRequestContext.delete(clientMessageId);
      nextTurn.is_streaming = false;
      nextTurn.is_streamError = this.persistentErrorMessage(
        error instanceof Error ? error : new Error(String(error))
      );
      ConversationHistoryHelper.updateTurn(nextTurn);
      logger.error('Unable to continue browser tool calls', error);
    }
  }

  public async recoverPendingWidgets(turn: IAnswer): Promise<void> {
    const ids = turn.pending_widget_tool_call_ids;
    const calls = turn.tool_calls ?? [];
    const key = `${turn.thread_id}:${turn.question_id}`;
    if (!ids?.length || !turn.thread_id || this.widgetRecovery || this.activeClientMessageId
      || this.attemptedWidgetRecoveries.has(key) || calls.length !== ids.length
      || new Set(ids).size !== ids.length || calls.some((call) => !ids.includes(call.id))) return;

    // Never invoke arbitrary JavaScript callbacks during history restoration.
    const rendered = calls.map((call) => ChatKitWidgetHelper.renderToolCall(call));
    if (rendered.some((result) => !result)) return;
    this.attemptedWidgetRecoveries.add(key);
    const clientMessageId = conversationConnection.createClientMessageId();
    let finish!: () => void;
    const done = new Promise<void>((resolve) => { finish = resolve; });
    const timer = setTimeout(() => {
      this.widgetRecovery = undefined;
      removeBusyApi(ApiTypes.streamPOSTQuestion);
      finish();
    }, 180000);
    this.widgetRecovery = { clientMessageId, done, finish: () => {
      clearTimeout(timer);
      this.widgetRecovery = undefined;
      finish();
    } };
    const nextTurn = ConversationHistoryHelper.addQuestion('', false, true);
    nextTurn.client_message_id = clientMessageId;
    addBusyApi(ApiTypes.streamPOSTQuestion);
    try {
      const accepted = await conversationConnection.sendQuestion(turn.thread_id, {
        type: 'tool', requestType: 'toolcall', recover_widget_tools: true,
        response_to_question_id: turn.question_id,
        query: JSON.stringify(calls.map((run, i) => ({ run, response: rendered[i]!.response }))),
      }, clientMessageId);
      nextTurn.request_id = accepted.request_id;
      this.persistentTurns.set(accepted.request_id, nextTurn);
    } catch (error) {
      this.widgetRecovery?.finish();
      removeBusyApi(ApiTypes.streamPOSTQuestion);
      // Another tab or a late original result may already have advanced the head.
      // Do not downgrade transport, rebase the result or retry an ambiguous POST.
      nextTurn.is_streaming = false;
      nextTurn.hideOnChat = true;
      ConversationHistoryHelper.updateTurn(nextTurn);
      logger.warn('Pending widget recovery was not accepted', error);
      if ((error as any)?.status === 409) await new ThreadHandler().get(true);
    }
  }

  private async buildToolResponses(
    toolCalls: IToolCall[],
    gqa_functions: any,
    suppliedResults: ReadonlyMap<string, unknown> = new Map()
  ): Promise<IToolResponse[]> {
    return Promise.all(
      toolCalls.map(async (toolCall) => {
        if (suppliedResults.has(toolCall.id)) {
          return {
            response: suppliedResults.get(toolCall.id),
            run: toolCall,
          };
        }
        if (!toolCall.function) {
          return { response: 'ERROR', run: toolCall };
        }

        const widgetResult = ChatKitWidgetHelper.renderToolCall(toolCall);
        if (widgetResult) {
          return { response: widgetResult.response, run: toolCall };
        }

        const jsfunc =
          gqa_functions &&
          toolCall.function.name &&
          gqa_functions[toolCall.function.name];
        if (!jsfunc) {
          logger.warn('JavaScript function not found', toolCall.function.name);
          return { run: toolCall };
        }

        return {
          response: await jsfunc(
            toolCall.function.arguments
              ? JSON.parse(toolCall.function.arguments)
              : null
          ),
          run: toolCall,
        };
      })
    );
  }

  private renderWidgetToolCalls(
    turn: IAnswer | undefined,
    toolCalls: IToolCall[]
  ): void {
    if (!turn) return;

    const widgets: IChatKitWidgetInstance[] = [];
    toolCalls.forEach((toolCall) => {
      const rendered = ChatKitWidgetHelper.renderToolCall(toolCall);
      if (rendered) {
        rendered.instance.questionId = turn.question_id;
        widgets.push(rendered.instance);
      }
    });

    if (!widgets.length) return;

    const existingOnTurn = new Set(
      (turn.widgets ?? []).map((widget) => widget.toolCallId)
    );
    const existingInThread = new Set(
      get(state_conversationResults)
        .filter((candidate) => candidate !== turn)
        .flatMap((candidate) => candidate.widgets ?? [])
        .map((widget) => widget.toolCallId)
        .filter((toolCallId): toolCallId is string => !!toolCallId)
    );
    const conversationTurns = get(state_conversationResults);
    const turnIndex = conversationTurns.indexOf(turn);
    let interactionStart = turnIndex;
    while (
      interactionStart > 0 &&
      !conversationTurns[interactionStart]?.question?.trim()
    ) {
      interactionStart -= 1;
    }
    const existingRenderKeys = new Set(
      [
        ...(turn.widgets ?? []),
        ...(turnIndex >= 0
          ? conversationTurns
              .slice(interactionStart, turnIndex)
              .flatMap((candidate) => candidate.widgets ?? [])
          : []),
      ].map((widget) => ChatKitWidgetHelper.getRenderKey(widget))
    );
    const accepted = new Set<string>();
    const acceptedRenderKeys = new Set<string>();
    const next = widgets.filter((widget) => {
      const toolCallId = widget.toolCallId;
      const renderKey = ChatKitWidgetHelper.getRenderKey(widget);
      if (
        existingRenderKeys.has(renderKey) ||
        acceptedRenderKeys.has(renderKey)
      ) {
        return false;
      }
      acceptedRenderKeys.add(renderKey);
      if (!toolCallId) return true;
      if (
        existingOnTurn.has(toolCallId) ||
        existingInThread.has(toolCallId) ||
        accepted.has(toolCallId)
      ) {
        return false;
      }
      accepted.add(toolCallId);
      return true;
    });
    if (!next.length) return;

    turn.widgets = [...(turn.widgets ?? []), ...next];
    ChatKitWidgetCache.saveWidgets(
      turn.thread_id || ConversationHistoryHelper.getThreadId() || turn.question_id,
      turn.question_id,
      turn.widgets
    );
    ConversationHistoryHelper.updateTurn(turn);
  }

}
